import React, { createContext, useContext, useEffect } from 'react';
import { useJsApiLoader } from '@react-google-maps/api';
import { GOOGLE_MAPS_API_KEY, APP_CONFIG } from '../../config/appConfig';

const LIBRARIES = APP_CONFIG.map.libraries;

const GoogleMapsContext = createContext({ isLoaded: false, loadError: null });

export const useGoogleMaps = () => useContext(GoogleMapsContext);

/**
 * Loads the Google Maps JS API exactly once for the whole app.
 *
 * IMPORTANT: This provider must NEVER block the rest of the app from
 * rendering. Previously the app was wrapped in <LoadScript>, which gates its
 * children until Maps finishes loading — so on the mobile APK (where the key
 * can be referrer/package-restricted or the native webview is slow) the user
 * got stuck on a "failed to load Google Maps" screen right after the splash.
 *
 * Now we use the non-blocking `useJsApiLoader` hook and always render the app.
 * Screens that actually need a map read `{ isLoaded, loadError }` from context
 * and show their own fallback when Maps is unavailable.
 */
export const GoogleMapsProvider = ({ children }) => {
  const { isLoaded, loadError } = useJsApiLoader({
    id: 'yachangu-google-maps',
    googleMapsApiKey: GOOGLE_MAPS_API_KEY || '',
    libraries: LIBRARIES,
  });

  useEffect(() => {
    if (loadError) {
      console.error('❌ Google Maps failed to load (app continues without maps):', loadError);
    }
  }, [loadError]);

  // Never block the app on Maps. Always render children; map-heavy screens
  // read `{ isLoaded, loadError }` from context and render their own fallback
  // until Maps is ready. (A blocking loader here could hang the whole app —
  // including the login screen — if the Maps script is slow or restricted.)
  return (
    <GoogleMapsContext.Provider value={{ isLoaded, loadError: loadError || null }}>
      {children}
    </GoogleMapsContext.Provider>
  );
};

export default GoogleMapsProvider;
